import axios from "axios";
import { Vehicle } from "../../../state/types";



export interface MediaFile {
  file: File;
  mediaGroup: string;
}

/**
 * Interface for the request to update image ordering in DynamoDB
 */
interface UpdateImageOrderRequest {
  pk: string;
  sk?: string;  // Optional sort key in case it can't be derived from pk
  imageType: string;
  imageArray: any[];
}

/**
 * Interface for the request to update image tag in DynamoDB
 */
interface UpdateImageTagRequest {
  pk: string;
  sk?: string;  // Optional sort key in case it can't be derived from pk
  imageType: string;
  imageIndex: number;
  newTag: string;
}

/**
 * Interface for the request to delete an image from DynamoDB
 */
interface DeleteImageRequest {
  pk: string;
  sk?: string;  // Optional sort key in case it can't be derived from pk
  imageType: string;
  imageIndex: number;
}


export const imageTypeMap: Record<string, string> = {
  'imageExterior': 'exterior_raw',
  'imageManual': 'manual_raw',
  'imagePano': 'pano_raw',
  'imageFeatured': 'featured',
  'imageMask': 'masking',
  'imageCrop': 'cropping',
  'imageUndercarriage': 'undercarriage_raw'
};

const s3FolderMap: Record<string, string> = {
  'imageExterior': 'img_ext',
  'imagePano': 'img_pano',
  'imageManual': 'img_man',
  'imageMask': 'img_mask',
  'imageCrop': 'img_crop',
  'imageFeatured': 'img_featured',
  'imageBackgroundReplaced': 'img_bgr',
  'imageUndercarriage': 'img_uc',
};

/**
 * Uploads a single image to S3 using presigned URL
 */
const uploadImage = async (vehicle: Vehicle, image: MediaFile, uploadIdx: number) => {
  // Get the appropriate backend image type
  const targetType = imageTypeMap[image.mediaGroup] || 'exterior_raw';

  // Create a meaningful filename based on assetIdentifier and sequential number
  const fileName = `${vehicle.assetIdentifier}-${String(uploadIdx + 1).padStart(2, '0')}.jpg`;

  const requestBody = {
    clientName: vehicle.clientIdentifier,
    pk: vehicle.pk,
    fileName: fileName,
    target: targetType,
    contentType: image.file.type
  };

  const headers = {
    Authorization: 'bwenvdev',
    'Content-Type': 'application/json',
  };

  try {
    // Step 1: Get presigned URL
    const presignedResponse = await axios.put(
      `${process.env.REACT_APP_UMS_API_BASE_URL}/asset/upload`,
      requestBody,
      { headers }
    );

    const presignedUrl = presignedResponse.data.presignedUrl;

    // Step 2: Upload file to S3 using presigned URL
    await axios.put(presignedUrl, image.file, {
      headers: {
        'Content-Type': image.file.type || 'image/jpeg'
      }
    });

    return {
      success: true,
      fileName,
      imageGroup: image.mediaGroup
    };
  } catch (error) {
    console.error("Upload error:", error);
    return {
      success: false,
      error
    };
  }
};

/**
 * Updates the image order in DynamoDB
 */
const updateImageOrder = async (vehicle: Vehicle, imageType: string, imageArray: any[]) => {
  // Extract the sort key from the vehicle asset ID
  const assetIdParts = (vehicle.pk).split('-');
  let sk = '';

  if (assetIdParts.length > 1) {
    sk = assetIdParts[1];
  }

  const requestBody: UpdateImageOrderRequest = {
    pk: vehicle.pk,
    sk: sk,  // Include the sort key explicitly
    imageType,
    imageArray
  };

  const headers = {
    Authorization: 'bwenvdev',
    'Content-Type': 'application/json',
  };

  try {
    // Configure axios for CORS
    const response = await axios.put(
      `${process.env.REACT_APP_UMS_API_BASE_URL}/asset/updateImageOrder`,
      requestBody,
      {
        headers,
        // Add withCredentials if you're using cookies for authorization
        // withCredentials: true
      }
    );

    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.error("Error updating image order:", error);
    return {
      success: false,
      error
    };
  }
};

/**
 * Updates an individual image tag in DynamoDB
 */
const updateImageTag = async (vehicle: Vehicle, imageType: string, imageIndex: number, newTag: string) => {
  // Extract the sort key from the vehicle asset ID
  const assetIdParts = (vehicle.assetId || vehicle.assetId).split('-');
  let sk = '';

  if (assetIdParts.length > 1) {
    sk = assetIdParts[1];
  }

  const requestBody: UpdateImageTagRequest = {
    pk: vehicle.pk,
    sk: sk,  // Include the sort key explicitly
    imageType,
    imageIndex,
    newTag
  };

  const headers = {
    Authorization: 'bwenvdev',
    'Content-Type': 'application/json',
  };

  try {
    const response = await axios.put(
      `${process.env.REACT_APP_UMS_API_BASE_URL}/asset/updateImageTag`,
      requestBody,
      { headers }
    );

    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.error("Error updating image tag:", error);
    return {
      success: false,
      error
    };
  }
};

/**
 * Deletes an image from DynamoDB array
 */
const deleteImage = async (vehicle: Vehicle, imageType: string, imageIndex: number) => {
  // Extract the sort key from the vehicle asset ID
  const assetIdParts = (vehicle.assetId || vehicle.assetId).split('-');
  let sk = '';

  if (assetIdParts.length > 1) {
    sk = assetIdParts[1];
  }

  const requestBody: DeleteImageRequest = {
    pk: vehicle.pk,
    sk: sk,  // Include the sort key explicitly
    imageType,
    imageIndex
  };

  const headers = {
    Authorization: 'bwenvdev',
    'Content-Type': 'application/json',
  };

  try {
    const response = await axios.delete(
      `${process.env.REACT_APP_UMS_API_BASE_URL}/asset/deleteImage`,
      {
        headers,
        data: requestBody  // DELETE requests need data in the config object
      }
    );

    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.error("Error deleting image:", error);
    return {
      success: false,
      error
    };
  }
};

const constructImageObject = (bucket: string, fullS3Key: string, tag: string) => {
  const cloudfrontUrl = `https://assets.dev.bwiportal.com/${fullS3Key}`; // Replace with your CloudFront domain
  const s3Url = `https://${bucket}.s3.amazonaws.com/${fullS3Key}`;
  const s3Uri = `s3://${bucket}/${fullS3Key}`;

  return {
    imageTag: tag,
    imageUrl: s3Url,
    imageUrlCf: cloudfrontUrl,
    imageUrlS3: s3Uri,
  };
};

/**
 * Comprehensive function to handle both uploading new images and updating order
 */
const uploadAndProcessImages = async (
  vehicle: Vehicle,
  uploadedFiles: MediaFile[],
  orderedImageTypes: Record<string, any[]>
) => {

  const uploadPromises = uploadedFiles.map((item, idx) =>
    uploadImage(vehicle, item, idx)
  );
  const uploadResults = await Promise.all(uploadPromises);
  const successfulUploads = uploadResults.filter(r => r.success);

  const finalImageArrays: Record<string, any[]> = {};

  for (const imageType in orderedImageTypes) {
    const imageArray = orderedImageTypes[imageType];

    finalImageArrays[imageType] = imageArray.map(image => {
      if (image.isNew) {
        const uploadResult = successfulUploads.shift();

        if (uploadResult) {
          const bucket = vehicle.s3bucket || ''
          const stripped_pk = vehicle.pk.replace("asset#", "");
          const targetType = s3FolderMap[uploadResult.imageGroup || ''] || 'exterior_raw';
          const fullS3Key = `${vehicle.clientIdentifier}/${stripped_pk}/${targetType}/${uploadResult.fileName}`;

          return constructImageObject(bucket, fullS3Key, image.imageTag || 'New Image');
        }
        return null;
      } else {
        return image;
      }
    }).filter(Boolean);
  }

  const orderUpdatePromises = Object.entries(finalImageArrays).map(([imageType, imageArray]) =>
    updateImageOrder(vehicle, imageType, imageArray)
  );

  const orderResults = await Promise.all(orderUpdatePromises);

  // Step 4: Return combined results
  return {
    uploads: uploadResults,
    orderUpdates: orderResults
  };
};

export { uploadImage, updateImageOrder, updateImageTag, deleteImage, uploadAndProcessImages };
export default uploadAndProcessImages;